import React, { Fragment, useEffect, useRef } from "react"
import { Hole, TeamRound, TeamRoundPlayer } from "../types"
import { formatScore, teamRoundHoles } from "../util"

interface ScorecardProps {
  numberOfHoles: number
  holes: Hole[]
  teamRound: TeamRound
  holeIndex: number
  onScoreCellPress?: (holeIndex: number, player: TeamRoundPlayer) => void
}

export default function Scorecard({
  numberOfHoles,
  holes,
  teamRound,
  holeIndex,
  onScoreCellPress
}: ScorecardProps) {
  const ref = useRef<HTMLDivElement>()
  const teamHoles = teamRoundHoles(holes, numberOfHoles, teamRound.startHoleNumber)
  const frontNine = teamHoles.slice(0, 9)
  const backNine = numberOfHoles === 18 ? teamHoles.slice(9, 18) : []

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = holeIndex >= 9 ? 300 : 0
    }
  }, [holeIndex])

  if (numberOfHoles === 18) {
    return (
      <div id={`scorecard-${teamRound.id}`} ref={ref} style={{ marginBottom: "1rem" }}>
        <NineHoleTable
          holes={frontNine}
          teamRound={teamRound}
          holeIndexOffset={0}
          onScoreCellPress={onScoreCellPress}
          showTotal={false}
        />
        <NineHoleTable
          holes={backNine}
          teamRound={teamRound}
          holeIndexOffset={9}
          onScoreCellPress={onScoreCellPress}
          showTotal
          previousHoles={frontNine}
        />
      </div>
    )
  }

  return (
    <div id={`scorecard-${teamRound.id}`} ref={ref} style={{ marginBottom: "1rem", overflow: "scroll hidden" }}>
      <NineHoleTable
        holes={frontNine}
        teamRound={teamRound}
        holeIndexOffset={0}
        onScoreCellPress={onScoreCellPress}
        showTotal={false}
      />
    </div>
  )
}

interface NineHoleTableProps {
  holes: Hole[]
  teamRound: TeamRound
  holeIndexOffset: number
  onScoreCellPress?: (holeIndex: number, player: TeamRoundPlayer) => void
  showTotal: boolean
  previousHoles?: Hole[]
}

function NineHoleTable({
  holes,
  teamRound,
  holeIndexOffset,
  onScoreCellPress,
  showTotal,
  previousHoles = []
}: NineHoleTableProps) {
  const { players } = teamRound
  let totalPar = 0
  let runningTotal = 0
  const previousPar = previousHoles.reduce((sum, hole) => sum + hole.par, 0)

  return (
    <div style={{ marginBottom: showTotal ? 0 : "1rem", overflow: "scroll hidden" }}>
      <table className="scorecard">
        <tbody>
          <tr>
            <th className="left"></th>
            {holes.map((hole, i) => (
              <Fragment key={hole.number}>
                <th>{hole.number}</th>
                {i === 8 ? <th key="out">{holeIndexOffset === 0 ? (showTotal ? "Out" : "") : "In"}</th> : null}
                {i === 8 && showTotal ? <th key="total">&nbsp;</th> : null}
              </Fragment>
            ))}
          </tr>
          <tr>
            <td className="left">Par</td>
            {holes.map((hole) => {
              totalPar = totalPar + hole.par
              return (
                <td className="score" key={hole.id}>
                  {hole.par}
                </td>
              )
            })}
            <td className="score">{totalPar}</td>
            {showTotal ? <td className="score">{previousPar + totalPar}</td> : null}
          </tr>
          {players
            .filter((p) => !p.canceled)
            .map((p) => (
              <tr key={p.id}>
                <td className="left">{p.user.abbreviatedName}</td>
                {holes.map((hole, index) => (
                  <HoleScoreCell
                    key={hole.id}
                    player={p}
                    hole={hole}
                    holeIndex={index + holeIndexOffset}
                    onPress={onScoreCellPress}
                  />
                ))}
                <td>{sumOfGrossScores(holes, p)}</td>
                {showTotal ? <td>{sumOfGrossScores([...previousHoles, ...holes], p)}</td> : null}
              </tr>
            ))}
          <tr>
            <td className="left">Net</td>
            {holes.map((hole) => {
              const score = teamRound.scores?.get(hole.id)?.netScoreRelativeToPar
              if (score) {
                runningTotal = runningTotal + score
              }
              return (
                <td className="score" key={hole.id}>
                  {score || score === 0 ? formatScore(score) : ""}
                </td>
              )
            })}
            <td style={{ fontWeight: "bold" }}>{formatScore(runningTotal)}</td>
            {showTotal ? (
              <td style={{ fontWeight: "bold" }}>
                {formatScore(
                  [...previousHoles, ...holes].reduce((sum, hole) => {
                    const score = teamRound.scores?.get(hole.id)?.netScoreRelativeToPar
                    return score || score === 0 ? sum + score : sum
                  }, 0)
                )}
              </td>
            ) : null}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface HoleScoreCellProps {
  player: TeamRoundPlayer
  hole: Hole
  holeIndex: number
  onPress?: (holeIndex: number, player: TeamRoundPlayer) => void
}

function HoleScoreCell({ player, hole, holeIndex, onPress }: HoleScoreCellProps) {
  const score = player.scores?.get(hole.id)
  const tappable = !!onPress
  const handleClick = tappable
    ? () => {
        onPress(holeIndex, player)
      }
    : undefined

  if (!score) {
    return (
      <td
        className={tappable ? "score score--tappable" : undefined}
        role={tappable ? "button" : undefined}
        onClick={handleClick}
      />
    )
  }

  const value =
    score.netScore < score.grossScore ? (
      <span>
        <sup>{score.grossScore}</sup>&frasl;<sub>{score.netScore}</sub>
      </span>
    ) : (
      <span>{score.grossScore}</span>
    )

  const className = ["score", tappable ? "score--tappable" : null, score.usedForTeamScore ? "spark-orange-border" : null]
    .filter(Boolean)
    .join(" ")

  return (
    <td className={className} role={tappable ? "button" : undefined} onClick={handleClick}>
      {score.usedForTeamScore ? <strong className="spark-orange">{value}</strong> : value}
    </td>
  )
}

function sumOfGrossScores(holes: Hole[], player: TeamRoundPlayer) {
  let score = 0
  for (let hole of holes) {
    score = score + (player.scores?.get(hole.id)?.grossScore || 0)
  }
  return score || null
}
